import { useEffect, useState } from 'react';
import {
    type Route,
    type SceneRendererProps,
    TabView,
} from 'react-native-tab-view';
import { isWeb, KeyboardCompatibleBottomSheet, YStack } from 'smoothie';

import type { PartialBill } from '../../types/Bill';
import {
    AccountInput,
    CadenceInput,
    CategoryInput,
    DisplayNameInput,
    PayeeInput,
    TimingInput,
} from './BillDetailsEditorSheetInputs';

export enum BillDetailIndex {
    DISPLAY_NAME = 0,
    PAYEE = 1,
    ACCOUNT = 2,
    CADENCE = 3,
    TIMING = 4,
    CATEGORY = 5,
}

const routes = [
    { key: BillDetailIndex.DISPLAY_NAME.toString(), title: '' },
    { key: BillDetailIndex.PAYEE.toString(), title: '' },
    { key: BillDetailIndex.ACCOUNT.toString(), title: '' },
    { key: BillDetailIndex.CADENCE.toString(), title: '' },
    { key: BillDetailIndex.TIMING.toString(), title: '' },
    { key: BillDetailIndex.CATEGORY.toString(), title: '' },
];

interface DetailsEditorTabViewProps {
    bill: PartialBill;
    currentBillDetailIndex: BillDetailIndex;
    onSubmit: (updatedBill: PartialBill) => void;
    onClose: () => void;
}

const DetailsEditorTabView = ({
    bill,
    currentBillDetailIndex,
    onSubmit,
    onClose,
}: DetailsEditorTabViewProps) => {
    const [index, setIndex] = useState(currentBillDetailIndex);

    useEffect(() => {
        setIndex(currentBillDetailIndex);
    }, [currentBillDetailIndex]);

    const onInputSubmit = (updatedBill: PartialBill) => {
        onSubmit(updatedBill);
        onClose();
    };

    const onBack = () => {
        if (index > 0) {
            setIndex((i) => i - 1);
        } else {
            onClose();
        }
    };

    const renderScene = ({ route }: SceneRendererProps & { route: Route }) => {
        const props = { bill, onSubmit: onInputSubmit, onBack };
        switch (Number(route.key) as BillDetailIndex) {
            case BillDetailIndex.DISPLAY_NAME:
                return <DisplayNameInput {...props} />;
            case BillDetailIndex.PAYEE:
                return <PayeeInput {...props} />;
            case BillDetailIndex.ACCOUNT:
                return <AccountInput {...props} />;
            case BillDetailIndex.CADENCE:
                return <CadenceInput {...props} />;
            case BillDetailIndex.TIMING:
                return <TimingInput {...props} />;
            case BillDetailIndex.CATEGORY:
                return <CategoryInput {...props} />;
            default:
                return <></>;
        }
    };

    return (
        <YStack minHeight={400}>
            <TabView
                navigationState={{ index, routes }}
                renderTabBar={() => null}
                renderScene={renderScene}
                onIndexChange={setIndex}
                swipeEnabled={!isWeb}
                commonOptions={{
                    sceneStyle: {
                        paddingHorizontal: 4, // prevents inputs from overflowing container
                    },
                }}
            />
        </YStack>
    );
};

interface DetailsEditorSheetProps {
    bill: PartialBill;
    currentBillDetailIndex: BillDetailIndex;
    isOpen: boolean;
    onSubmit: (updatedBill: PartialBill) => void;
    onClose: () => void;
}

const DetailsEditorSheet = ({
    bill,
    currentBillDetailIndex,
    isOpen,
    onSubmit,
    onClose,
}: DetailsEditorSheetProps) => {
    return (
        isOpen && (
            <KeyboardCompatibleBottomSheet
                onDismiss={onClose}
                showCloseButton={false}
            >
                <DetailsEditorTabView
                    bill={bill}
                    currentBillDetailIndex={currentBillDetailIndex}
                    onSubmit={onSubmit}
                    onClose={onClose}
                />
            </KeyboardCompatibleBottomSheet>
        )
    );
};

export default DetailsEditorSheet;
